import { useContext } from "react";

// Import from relative path - will work if using from within the application
// This import should be updated if the file structure changes
import { featureFlagContext } from "app/(new)/FeatureFlagProvider";
import { getFeatureFlagValue } from "state/featureFlags";

import { useSelector } from "./redux";

import type { FeatureFlags as ContextFeatureFlags } from "app/(new)/FeatureFlagProvider";
import type { CLOUD_API } from "@skydio/api_util/src";

// Type that can represent both feature flag sources
type FeatureFlagKey = keyof CLOUD_API.FeatureFlags | keyof ContextFeatureFlags;

/**
 * Unified hook for feature flags that works with both context and redux state
 * @param feature The feature flag key to check
 * @returns The value of the feature flag
 */
export const useFeatureFlag = <T extends FeatureFlagKey>(feature: T): boolean => {
  // Always call both hooks to maintain React hooks calling order rules
  const contextFlags = useContext(featureFlagContext);

  // If we have context flags available, prefer those
  if (contextFlags) {
    return Boolean(contextFlags[feature as keyof ContextFeatureFlags]);
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const reduxValue = useSelector(state =>
    getFeatureFlagValue(state, feature as keyof CLOUD_API.FeatureFlags)
  );

  // Otherwise fall back to redux state
  return reduxValue;
};
